'use client';

import React, { useState, useEffect } from 'react';
import ServiceTierCard from './ServiceTierCard';
import PlanningProcessStep from './PlanningProcessStep';
import TestimonialCard from './TestimonialCard';
import ConsultationBooking from './ConsultationBooking';
import EmergencyProtocols from './EmergencyProtocols';

interface ServiceTier {
  id: string;
  name: string;
  description: string;
  features: string[];
  responseTime: string;
  availability: string;
  icon: string;
  isPopular?: boolean;
}

interface ProcessStep {
  id: number;
  title: string;
  description: string;
  duration: string;
  icon: string;
  details: string[];
}

interface Testimonial {
  id: string;
  memberInitials: string;
  memberTitle: string;
  expeditionType: string;
  quote: string;
  rating: number;
  year: number;
  avatar: string;
  alt: string;
}

const ConciergeServicesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState('services');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const serviceTiers: ServiceTier[] = [
  {
    id: '1',
    name: 'Essential Concierge',
    description: 'Comprehensive expedition planning and support',
    features: [
    'Expedition route planning and optimization',
    'Local guide and expert coordination',
    'Equipment and logistics management',
    'Cultural briefings and site preparation',
    'Basic emergency support protocols'],

    responseTime: 'Within 24 hours',
    availability: 'Business hours GMT',
    icon: 'MapIcon'
  },
  {
    id: '2',
    name: 'Elite Concierge',
    description: 'Premium planning with dedicated specialist',
    features: [
    'Dedicated concierge specialist assignment',
    'Real-time expedition monitoring',
    'VIP access arrangements and permissions',
    'Custom itinerary development',
    'Priority emergency response',
    'Post-expedition documentation support'],

    responseTime: 'Within 6 hours',
    availability: '16 hours daily',
    icon: 'StarIcon',
    isPopular: true
  },
  {
    id: '3',
    name: 'Platinum Concierge',
    description: 'White-glove service with 24/7 support',
    features: [
    'Personal concierge team (3+ specialists)',
    'Continuous 24/7 expedition oversight',
    'Exclusive site access negotiations',
    'Private transportation arrangements',
    'Immediate emergency response team',
    'Complete expedition documentation',
    'Legacy archive contribution support'],

    responseTime: 'Within 2 hours',
    availability: '24/7 Global Coverage',
    icon: 'ShieldCheckIcon'
  }];


  const planningSteps: ProcessStep[] = [
  {
    id: 1,
    title: 'Initial Consultation',
    description: 'Comprehensive discussion of expedition goals, preferences, and requirements with your dedicated concierge specialist.',
    duration: '2-3 hours',
    icon: 'ChatBubbleLeftRightIcon',
    details: [
    'Personal expedition history review',
    'Interest and expertise assessment',
    'Risk tolerance and physical capability evaluation',
    'Timeline and budget framework establishment']

  },
  {
    id: 2,
    title: 'Site Selection & Research',
    description: 'Detailed analysis of available sites matching your criteria, including exclusive access opportunities and institutional partnerships.',
    duration: '1-2 weeks',
    icon: 'MagnifyingGlassIcon',
    details: [
    'Vetted site database analysis',
    'Current accessibility and conditions assessment',
    'Permit and permission requirements research',
    'Local expert and guide identification']

  },
  {
    id: 3,
    title: 'Expedition Design',
    description: 'Custom itinerary development with detailed logistics, safety protocols, and contingency planning.',
    duration: '2-3 weeks',
    icon: 'DocumentTextIcon',
    details: [
    'Day-by-day itinerary creation',
    'Equipment and supply coordination',
    'Transportation and accommodation arrangements',
    'Emergency protocol establishment']

  },
  {
    id: 4,
    title: 'Pre-Departure Preparation',
    description: 'Final preparations including briefings, equipment checks, and last-minute coordination with local partners.',
    duration: '1 week',
    icon: 'CheckCircleIcon',
    details: [
    'Cultural and historical site briefings',
    'Equipment inspection and final preparations',
    'Local contact confirmation and coordination',
    'Emergency contact and protocol review']

  },
  {
    id: 5,
    title: 'Expedition Support',
    description: 'Continuous monitoring and support throughout your expedition with real-time assistance availability.',
    duration: 'Expedition length',
    icon: 'EyeIcon',
    details: [
    'Daily check-in and progress monitoring',
    'Real-time weather and condition updates',
    'Emergency response coordination if needed',
    'Local issue resolution and support']

  },
  {
    id: 6,
    title: 'Post-Expedition Services',
    description: 'Comprehensive follow-up including documentation, legacy contributions, and future expedition planning.',
    duration: '2-4 weeks',
    icon: 'ArchiveBoxIcon',
    details: [
    'Expedition documentation and archiving',
    'Member achievement recognition',
    'Legacy database contribution processing',
    'Future expedition recommendations and planning']

  }];


  const testimonials: Testimonial[] = [
  {
    id: '1',
    memberInitials: 'R.H.',
    memberTitle: 'Maritime Archaeologist',
    expeditionType: 'Mediterranean Wreck Sites',
    quote: 'The concierge team\'s attention to detail was extraordinary. They secured access to sites I\'d been trying to reach for years and coordinated with local authorities seamlessly.',
    rating: 5,
    year: 2024,
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18b455c05-1763292276996.png",
    alt: 'Professional headshot of middle-aged man with gray beard wearing navy blazer'
  },
  {
    id: '2',
    memberInitials: 'M.C.',
    memberTitle: 'Cultural Heritage Specialist',
    expeditionType: 'Andean Archaeological Survey',
    quote: 'Their emergency protocols saved our expedition when weather conditions deteriorated. The 24/7 support and local coordination were invaluable.',
    rating: 5,
    year: 2024,
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1eb0ee20c-1763294964632.png",
    alt: 'Professional woman with dark hair in business attire smiling confidently'
  },
  {
    id: '3',
    memberInitials: 'D.L.',
    memberTitle: 'Geological Survey Expert',
    expeditionType: 'Arctic Research Expedition',
    quote: 'The level of preparation and ongoing support exceeded all expectations. Every detail was anticipated and managed with remarkable professionalism.',
    rating: 5,
    year: 2023,
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a2a6c51c-1765466249743.png",
    alt: 'Distinguished older gentleman with silver hair in formal business suit'
  }];


  const tabs = [
  { id: 'services', label: 'Service Tiers', icon: 'SparklesIcon' },
  { id: 'process', label: 'Planning Process', icon: 'Cog6ToothIcon' },
  { id: 'testimonials', label: 'Member Experiences', icon: 'ChatBubbleLeftIcon' },
  { id: 'booking', label: 'Schedule Consultation', icon: 'CalendarIcon' },
  { id: 'emergency', label: 'Emergency Protocols', icon: 'ShieldExclamationIcon' }];


  if (!isHydrated) {
    return (
      <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:229:6" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="229" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:230:8" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="230" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%20p-8%22%7D" className="animate-pulse space-y-8 p-8">
          <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:231:10" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="231" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20rounded%20w-1%2F3%22%7D" className="h-8 bg-muted rounded w-1/3"></div>
          <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:232:10" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="232" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) =>
            <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:234:12" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="234" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-xl%22%7D" key={i} className="h-64 bg-muted rounded-xl"></div>
            )}
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:243:4" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="243" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-12%22%7D" className="space-y-12">
      {/* Tab Navigation */}
      <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:245:6" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="245" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22border-b%20border-border%22%7D" className="border-b border-border">
        <nav data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:246:8" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="246" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22flex%20space-x-8%20overflow-x-auto%22%7D" className="flex space-x-8 overflow-x-auto">
          {tabs.map((tab) =>
          <button data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:248:10" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="248" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-inter font-medium whitespace-nowrap transition-colors duration-200 ${
            activeTab === tab.id ?
            'border-accent text-accent' : 'border-transparent text-text-secondary hover:text-accent hover:border-accent/50'}`
            }>

              <span data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:256:14" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="256" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{tab.label}</span>
            </button>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:263:6" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="263" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-%5B600px%5D%22%7D" className="min-h-[600px]">
        {activeTab === 'services' &&
        <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:265:8" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="265" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%22%7D" className="space-y-8">
            <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:266:12" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="266" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" className="text-center">
              <h2 data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:267:14" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="267" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-playfair%20font-bold%20text-3xl%20text-primary%20mb-4%22%2C%22textContent%22%3A%22Concierge%20Service%20Tiers%22%7D" className="font-playfair font-bold text-3xl text-primary mb-4">
                Concierge Service Tiers
              </h2>
              <p data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:270:14" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="270" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20font-inter%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Choose%20the%20level%20of%20support%20that%20matches%20your%20expedition%20requirements%20and%20preferences%22%7D" className="text-text-secondary font-inter max-w-2xl mx-auto">
                Choose the level of support that matches your expedition requirements and preferences
              </p>
            </div>
            <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:274:12" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="274" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {serviceTiers.map((tier) =>
            <ServiceTierCard data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:276:12" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="276" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="ServiceTierCard" data-component-content="%7B%22elementName%22%3A%22ServiceTierCard%22%7D" key={tier.id} tier={tier} />
            )}
            </div>
          </div>
        }

        {activeTab === 'process' &&
        <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:283:8" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="283" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%22%7D" className="space-y-8">
            <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:284:12" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="284" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" className="text-center">
              <h2 data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:285:14" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="285" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-playfair%20font-bold%20text-3xl%20text-primary%20mb-4%22%2C%22textContent%22%3A%22Expedition%20Planning%20Process%22%7D" className="font-playfair font-bold text-3xl text-primary mb-4">
                Expedition Planning Process
              </h2>
              <p data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:288:14" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="288" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20font-inter%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Our%20systematic%20approach%20ensures%20every%20expedition%20is%20meticulously%20planned%20and%20expertly%20executed%22%7D" className="text-text-secondary font-inter max-w-2xl mx-auto">
                Our systematic approach ensures every expedition is meticulously planned and expertly executed
              </p>
            </div>
            <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:292:12" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="292" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%22%7D" className="max-w-4xl mx-auto">
              {planningSteps.map((step, index) =>
            <PlanningProcessStep data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:294:12" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="294" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="PlanningProcessStep" data-component-content="%7B%22elementName%22%3A%22PlanningProcessStep%22%7D"
              key={step.id}
              step={step}
              isLast={index === planningSteps.length - 1} />

            )}
            </div>
          </div>
        }

        {activeTab === 'testimonials' &&
        <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:305:8" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="305" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%22%7D" className="space-y-8">
            <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:306:12" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="306" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" className="text-center">
              <h2 data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:307:14" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="307" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-playfair%20font-bold%20text-3xl%20text-primary%20mb-4%22%2C%22textContent%22%3A%22Member%20Experiences%22%7D" className="font-playfair font-bold text-3xl text-primary mb-4">
                Member Experiences
              </h2>
              <p data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:310:14" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="310" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20font-inter%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Discover%20how%20our%20concierge%20services%20have%20enhanced%20expeditions%20for%20fellow%20society%20members%22%7D" className="text-text-secondary font-inter max-w-2xl mx-auto">
                Discover how our concierge services have enhanced expeditions for fellow society members
              </p>
            </div>
            <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:314:12" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="314" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-2%20xl%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
              {testimonials.map((testimonial) =>
            <TestimonialCard data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:316:12" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="316" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="TestimonialCard" data-component-content="%7B%22elementName%22%3A%22TestimonialCard%22%7D" key={testimonial.id} testimonial={testimonial} />
            )}
            </div>
          </div>
        }

        {activeTab === 'booking' &&
        <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:323:8" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="323" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%22%7D" className="max-w-4xl mx-auto">
            <ConsultationBooking data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:324:12" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="324" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="ConsultationBooking" data-component-content="%7B%22elementName%22%3A%22ConsultationBooking%22%7D" />
          </div>
        }

        {activeTab === 'emergency' &&
        <div data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:329:8" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="329" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-6xl%20mx-auto%22%7D" className="max-w-6xl mx-auto">
            <EmergencyProtocols data-component-id="src/app/concierge-services/components/ConciergeServicesInteractive.tsx:330:12" data-component-path="src/app/concierge-services/components/ConciergeServicesInteractive.tsx" data-component-line="330" data-component-file="ConciergeServicesInteractive.tsx" data-component-name="EmergencyProtocols" data-component-content="%7B%22elementName%22%3A%22EmergencyProtocols%22%7D" />
          </div>
        }
      </div>
    </div>);

};

export default ConciergeServicesInteractive;